import ExcelJS from 'exceljs';
import { downloadBlobFile } from '@/hooks/useFileDownload';
import {
  getChildAndYouthFieldMappings,
  getStudentTravelFieldMappings,
} from '@/components/Import/fieldsLegendData';
import { StudentFormType } from '@/hooks/useStudentFormSchema';

export interface TemplateColumn {
  header: string;
  example?: string;
  required?: boolean;
  description?: string;
  extraInfo?: string[];
}

export const downloadCSVTemplate = (
  filename: string,
  columns: TemplateColumn[],
): void => {
  // Excel on Swedish systems only splits CSV columns on ';'
  const headers = columns.map((col) => col.header).join(';');
  const examples = columns.map((col) => col.example || '').join(';');

  // Add UTF-8 BOM for Excel to recognize the encoding correctly
  const BOM = '\uFEFF';
  const csvContent = `${BOM}${headers}\n${examples}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlobFile(blob, filename);
};

export const downloadExcelTemplate = async (
  filename: string,
  columns: TemplateColumn[],
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Resenärer');

  const headers = columns.map((col) => col.header);
  worksheet.addRow(headers);

  const examples = columns.map((col) => col.example || '');
  worksheet.addRow(examples);

  worksheet.columns = columns.map((col) => ({
    width: Math.max(col.header.length, col.example?.length || 0) + 2,
    style: { numFmt: '@' },
  }));

  worksheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.numFmt = '@';
    });
  });

  const headerRow = worksheet.getRow(1);
  columns.forEach((col, index) => {
    if (
      col.required === undefined &&
      !col.description &&
      !col.extraInfo?.length
    ) {
      return;
    }
    const cell = headerRow.getCell(index + 1);
    if (col.required === false) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD9D9D9' },
      };
    }
    const requiredLabel =
      col.required === undefined
        ? ''
        : col.required
          ? 'Obligatorisk'
          : 'Valfri';
    const noteParts = [
      requiredLabel,
      col.description ?? '',
      ...(col.extraInfo ?? []),
    ].filter(Boolean);
    if (noteParts.length > 0) {
      cell.note = noteParts.join('\n\n');
    }
  });

  const excelBuffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  downloadBlobFile(blob, filename);
};

export const getTravellerTemplateColumns = (
  includeDepartment: boolean,
): TemplateColumn[] => {
  const columns: TemplateColumn[] = [
    {
      header: 'E-post',
      example: 'exempel@example.com',
    },
    {
      header: 'Förnamn',
      example: 'Anna',
    },
    {
      header: 'Efternamn',
      example: 'Andersson',
    },
    {
      header: 'Personnummer',
      example: '19900101-1234',
    },
    {
      header: 'E-post i appen / Mina sidor',
      example: 'anna.andersson@example.com',
    },
    {
      header: 'Mobilnummer (format +4670XXXXXXX)',
      example: '+46701234567',
    },
  ];

  if (includeDepartment) {
    columns.push({
      header: 'Avdelning',
      example: 'IT-avdelningen',
    });
  }

  return columns;
};

export const getStudentTemplateColumns = (
  type: StudentFormType,
  disableHalfProduct = false,
  translateExtraInfo?: (id: string) => string,
): TemplateColumn[] => {
  const mappings =
    type === 'child-and-youth'
      ? getChildAndYouthFieldMappings(disableHalfProduct)
      : getStudentTravelFieldMappings(disableHalfProduct);

  return mappings.map((field) => ({
    header: field.column,
    example: field.example,
    required: field.required,
    description: field.description,
    extraInfo: translateExtraInfo
      ? field.extraInfo?.map(translateExtraInfo)
      : field.extraInfo,
  }));
};
